<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import type { ConsumablesModeType } from '@/types/consumables'
import { socketEmitAsync } from '@/utils/web-socket'
import { monsterNameMap } from '@/views/pet-ranch/data'
import { treasuryInfoList } from '@/views/treasury/data'
import { computed, onMounted } from 'vue'
import { ref } from 'vue'
import { reactive } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { formatNumberWithCommas } from '@/utils'
import { oneYearRechargeReward, petAttrIntro } from '../data'
import type { AccRechargeRewardType } from '@/types/mall'
import { useCommonStore } from '@/stores/modules/common'
import type { PetType } from '@/types/pet'

const userStore = useUserStore()
const commonStore = useCommonStore()

const curTab = ref('gift')

const oneYearPetList = computed(() => {
  return Object.entries(monsterNameMap)
    .filter((item) => [601, 602, 603, 604, 605, 606].includes(Number(item[0])))
    .filter((item) => {
      const monsterIdList = userStore.petList.map((item) => item.monsterId)
      if (!monsterIdList.includes(Number(item[0]))) {
        return true
      }
    })
    .map((item) => ({
      label: `${item[1]}（${petAttrIntro[item[0]]}）`,
      value: Number(item[0]),
    }))
})

const form = reactive<
  {
    petId?: number
    consumableChoice1: number
    consumableChoice2: number
    consumableChoice3: number
    consumableChoice4: number
    consumableChoice5: number
  } & Record<string, number | undefined>
>({
  petId: undefined,
  consumableChoice1: 0,
  consumableChoice2: 0,
  consumableChoice3: 0,
  consumableChoice4: 0,
  consumableChoice5: 0,
})

const oneYearGiftConsumables = ref<Partial<Record<ConsumablesModeType, number>>[][]>([])
async function getOneYearGiftConsumables() {
  const res = await socketEmitAsync<Partial<Record<ConsumablesModeType, number>>[][]>({ type: 'getOneYearGiftConsumables' })
  oneYearGiftConsumables.value = res
  if (userStore.advanceLevel === 4) {
    form.consumableChoice1 = 1
    form.consumableChoice2 = 1
    form.consumableChoice3 = 1
    form.consumableChoice4 = 1
    form.consumableChoice5 = 1
  }
}
function getConsumablesInfo(consumables: Partial<Record<ConsumablesModeType, number>>) {
  const key = Object.keys(consumables)[0]
  return `${treasuryInfoList.find((item) => item.key === key)?.name}：${Object.values(consumables)[0]}`
}
async function handleBuy() {
  if (!form.petId) return ElMessage.warning('请选择宠物')
  ElMessageBox.confirm('确定购买【新春纪念礼】吗？（花费 8,800 🔮水晶）', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { petId, ...consumableChoiceObjList } = form
      const consumableChoiceList = Object.entries(consumableChoiceObjList).map(([_key, value]) => value)
      const res = await socketEmitAsync<{ petInfo: PetType; consumables: Record<ConsumablesModeType, number> }>({
        type: 'buyOneYearGift',
        data: { count: 1, petId, consumableChoiceList },
      })
      const { petInfo, consumables } = res
      userStore.petList.push(petInfo)
      let key: ConsumablesModeType
      for (key in consumables) {
        userStore.consumables[key] += consumables[key]
      }
      form.petId = undefined
      userStore.crystal -= 8800
      userStore.gold += 8888888
      ElMessage.success('购买成功，奖励已发放至背包')
    })
    .catch(() => {})
}

const rechargeCount = ref(0)
const rechargeLevel = ref(0)
function handleGetRecharge() {
  socketEmitAsync<{ rechargeCount: number; rechargeLevel: number }>({
    type: 'getCelebrationRecharge',
  }).then((res) => {
    rechargeCount.value = res.rechargeCount
    rechargeLevel.value = res.rechargeLevel
  })
}
async function handleReceiveRechargeReward() {
  const res = await socketEmitAsync<number>({ type: 'receiveCelebrationRecharge' })
  rechargeLevel.value = res
  commonStore.getMailList()
  ElMessage.success('领取成功，请在邮件处查看奖励')
}
function getAccRechargeTrColor(item: AccRechargeRewardType) {
  if (item.needCount > rechargeCount.value) {
    return 'var(--el-color-danger-light-5)'
  } else if (item.level <= rechargeLevel.value) {
    return 'var(--el-color-warning-light-5)'
  } else {
    return 'var(--el-color-success-light-5)'
  }
}

onMounted(() => {
  getOneYearGiftConsumables()
  handleGetRecharge()
})
</script>

<template>
  <div class="one-year-gift">
    <p style="text-align: right; font-size: 12px; margin: 0 0 5px 0">🔮 {{ formatNumberWithCommas(userStore.crystal) }}</p>
    <el-tabs v-model="curTab" type="border-card">
      <el-tab-pane label="礼包" name="gift" class="pane">
        <div class="intro">
          <p class="title">新春纪念礼</p>
          <p class="price">售价：🔮 8,800</p>
          <p class="">限购：6</p>
        </div>
        <div class="choice border-wrap">
          <h3 style="margin-bottom: 4px">请选择您的奖励</h3>
          <p style="font-size: 12px; text-align: center">您仅会获得您选取的奖励</p>
          <el-form>
            <el-form-item label="魔物">
              <div style="display: flex; align-items: center; width: 100%">
                <el-select v-model="form.petId" placeholder="请选择 6阶魔物" style="flex: 1">
                  <el-option v-for="item of oneYearPetList" :value="item.value" :label="item.label">
                    <span class="antiquity">{{ item.label }}</span>
                  </el-option>
                </el-select>
                <div class="help-info" style="margin-left: 10px">
                  <el-popover placement="bottom" width="100%" trigger="click">
                    <template #reference>
                      <el-icon size="20"><QuestionFilled /></el-icon>
                    </template>
                    <div class="affix">
                      <h4>礼包魔物说明</h4>
                      <p>
                        1. 礼包内的<span class="pet">魔物</span>为 <span class="grow">6</span>阶<span class="antiquity">远古</span
                        >魔物，魔物名称后为其战斗属性倾向。
                      </p>
                      <p>2. 得到的魔物战斗评分约为<span class="grow">28</span>分。</p>
                      <p>
                        3. 若您拥有<span class="antiquity">小型水晶龙</span>，则按照对应属性的补偿保底战斗评分。举例：您拥有<span class="antiquity"
                          >小型水晶龙 - 攻击</span
                        >且其<span class="info">战斗评分</span>为<span class="grow">34</span>分时，购买<span class="antiquity">破军铁骑</span
                        >的战斗评分也为<span class="grow">34</span>分。
                      </p>
                      <p>4. 魔物名称后的括号为其<span class="info">属性倾向</span>，购买入背包后不会显示。</p>
                    </div>
                  </el-popover>
                </div>
              </div>
            </el-form-item>
            <el-form-item v-for="(item, index) of oneYearGiftConsumables" :label="`道具${index + 1}`">
              <el-radio-group v-model="form[`consumableChoice${index + 1}`]" class="radio-list">
                <el-radio v-for="(option, optionIndex) of item" :value="optionIndex" style="width: 150px">
                  <span>{{ getConsumablesInfo(option) }}</span>
                </el-radio>
              </el-radio-group>
            </el-form-item>
          </el-form>
          <h3>其它奖励</h3>
          <div class="other-reward">
            <p>💰 8,888,888</p>
            <p class="affix">
              购买 <span class="grow">6</span>份后可得：<span
                class="nickname"
                style="background: linear-gradient(to bottom, #FF4500 0%, #FF8C00 25%, #FFD700 50%, #FF6347 75%, #8B0000 100%)"
                >特殊昵称颜色</span
              >
            </p>
          </div>
        </div>
        <el-button type="success" plain style="margin-top: 10px; width: 100%" @click="handleBuy">立即购买</el-button>
      </el-tab-pane>
      <el-tab-pane label="赞助" name="sponsor" class="pane" lazy>
        <div class="intro">
          <p class="title">新春赞助礼</p>
          <p>周年期间赞助，可额外获赠🔮水晶</p>
        </div>
        <p style="font-size: 14px; margin: 10px 0 5px 0">您的赞助水晶：🔮 {{ formatNumberWithCommas(rechargeCount) }}（邮件内收到的水晶为准）</p>
        <table border style="text-align: center; width: 100%">
          <tr>
            <td>赞助档位</td>
            <td>奖励</td>
          </tr>
          <tr v-for="item of oneYearRechargeReward" :style="{ backgroundColor: getAccRechargeTrColor(item) }">
            <td>🔮 {{ formatNumberWithCommas(item.needCount) }}</td>
            <td>
              <template v-if="item.reward.crystal"> 🔮 {{ formatNumberWithCommas(item.reward.crystal) }} </template>
            </td>
          </tr>
        </table>
        <el-button type="success" plain @click="handleReceiveRechargeReward" style="width: 100%; margin-top: 10px">领取</el-button>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<style lang="scss" scoped>
.one-year-gift {
  .intro {
    text-align: center;
    font-size: 14px;
    p {
      margin: 5px 0;
    }
    .title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 10px;
    }
  }
  .choice {
    padding: 0 10px;
  }
  .other-reward {
    p {
      font-size: 14px;
      margin: 8px 0;
    }
  }
  .el-tabs {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    :deep() {
      .el-tabs__content {
        padding: 5px;
        flex: 1;
        .pane {
          height: 100%;
          overflow: auto;
          .pane-item {
            height: 100%;
            overflow: auto;
            display: flex;
            flex-flow: column nowrap;
          }
        }
      }
    }
  }
}
</style>
